import React, { useEffect, useRef, useState } from 'react'
import Naray8CommentItem from './CommentItem';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { postNarray8Comment } from '../../../redux/naray8/naray8Action';
import { useGetNaray8Comments } from '../../../hooks/api/useNaray8';
import { MdCancel } from 'react-icons/md';


const SkeletonComment = () => (
    <div className="animate-pulse space-y-4">
        {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-600" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-600 rounded w-1/3" />
                    <div className="h-3 bg-gray-600 rounded w-full" />
                    <div className="h-3 bg-gray-600 rounded w-5/6" />
                </div>
            </div>
        ))}
    </div>
);

const Naray8CommentInput = ({ narrateId, chapterId, parentComment, clearParent }) => {
    const queryClient = useQueryClient();
    const [newComment, setNewComment] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null)

    useEffect(() => {
        if (parentComment) {
            inputRef.current.focus()
        }
    }, [parentComment])

    const handlePostComment = async () => {
        if (!narrateId && !chapterId) return;
        if (!newComment.trim()) return;
        setLoading(true);
        try {
            const payload = {
                ...(narrateId ? { narrateId } : {}),
                ...(chapterId ? { chapterId } : {}),
                body: newComment,
                ...(parentComment ? { parentCommentId: parentComment?.commentId } : {})
            }
            await postNarray8Comment(payload)
            if (chapterId) {
                queryClient.invalidateQueries({ queryKey: ["naray8", "chapter", chapterId, "comments"] })
            } else {
                queryClient.invalidateQueries({ queryKey: ["naray8", narrateId, "comments"] })
            }
            setNewComment("")
            if (parentComment) clearParent?.()
        }
        catch (err) {
            toast.error(err.message)
        }
        finally {
            setLoading(false);
        }
    }

    return (
        <React.Fragment>
            <div className="bg-white/15 rounded-[10px] p-3">
                {parentComment &&
                    <div className='relative group bg-customDark rounded-[10px] mb-1 p-2 font-sora w-fit max-w-[300px]'>
                        <button
                            onClick={clearParent}
                            className='absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition duration-300 rounded-full bg-customDark shadow-black shadow-md'
                        >
                            <MdCancel />
                        </button>
                        <h3 className='italic'><span className='font-bold not-italic'>Replying</span> {parentComment.user.firstName} {parentComment.user.lastName}</h3>
                        <p className='font-thin italic line-clamp-1'>{parentComment.body}</p>
                    </div>
                }
                <textarea
                    rows={3}
                    ref={inputRef}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    className="w-full bg-transparent h-[101px] font-outfit font-medium resize-none outline-none text-sm text-white placeholder-[#E0DDFA]"
                />
            </div>
            <div className="flex mt-2">
                <button
                    disabled={loading}
                    onClick={handlePostComment}
                    className="h-9 px-6 bg-white/15 rounded-[10px] text-sm font-outfit font-medium"
                >
                    {loading ? "Posting..." : "Post"}
                </button>
            </div>
        </React.Fragment>
    )
}

function Narray8Comment({ narrateId, chapterId, authorId }) {
    const { data, isLoading } = useGetNaray8Comments({ narrateId, chapterId });
    const [parentComment, setParentComment] = useState(null)
    const comments = data?.pages?.flatMap((comment) => comment.data) || []


    return (
        <section className="px-4 sm:px-20 mt-4 pb-32">

            <Naray8CommentInput narrateId={narrateId} chapterId={chapterId} parentComment={parentComment} clearParent={() => setParentComment(null)} />

            <div className="mt-8 space-y-8">
                {(comments.length === 0 && isLoading) ? (
                    <SkeletonComment />
                ) :
                    (!isLoading && comments?.length === 0) || !comments ?
                        (
                            <div className='font-sora'>No comment for this {chapterId ? "chapter" : "book"} yet</div>
                        )
                        :
                        (
                            comments.map((comment) => (
                                <Naray8CommentItem
                                    key={comment.commentId}
                                    comment={comment}
                                    onReply={setParentComment}
                                    authorId={authorId}
                                />
                            ))
                        )
                }
            </div>
        </section>
    )
}

export default Narray8Comment
